import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MasteryThree } from '../../redux/actions/wordsAction'
import { WordsLevelFilters } from '../../redux/reducers/wordsReducer'

interface Props {
  search: string
  mastery: string[]
  levels: WordsLevelFilters
  struggling: boolean
  onSearchChange: (s: string) => void
  onMasteryChange: (m: string[]) => void
  onLevelsChange: (l: Partial<WordsLevelFilters>) => void
  onStrugglingChange: (on: boolean) => void
}

// Labels are translation keys.
const masteryChips: { label: string; value: MasteryThree }[] = [
  { label: 'words:mastery.unknown', value: 'unknown' },
  { label: 'words:mastery.learning', value: 'learning' },
  { label: 'words:mastery.known', value: 'mastered' },
]

// Exam names are proper names: the same in every language.
const EXAM = { hsk: 'HSK', newHsk: 'HSK 3.0', tocfl: 'TOCFL' }
// Not copy: the clear and remove marks.
const CROSS = '×'

// Level catalogues mirror the values stored in `language_dictionary.test_level`.
// Keeping the labels here keeps the filter UI independent from the backend
// constants (which use different vocabularies — e.g. 'TOFCL', '7-9').
const HSK_LEVELS = ['1', '2', '3', '4', '5', '6']
const NEW_HSK_LEVELS = ['1', '2', '3', '4', '5', '6', '7-9']
const TOCFL_LEVELS = [
  { value: 'A (1級)', short: 'A1' },
  { value: 'A (2級)', short: 'A2' },
  { value: 'B (3級)', short: 'B3' },
  { value: 'B (4級)', short: 'B4' },
  { value: 'C (5級)', short: 'C5' },
  { value: 'C (6級)', short: 'C6' },
]

const toggleIn = (arr: string[], val: string): string[] =>
  arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]

const WordsFilters: React.FC<Props> = ({
  search,
  mastery,
  levels,
  struggling,
  onSearchChange,
  onMasteryChange,
  onLevelsChange,
  onStrugglingChange,
}) => {
  const { t } = useTranslation('words')
  const [localSearch, setLocalSearch] = useState(search)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [levelOpen, setLevelOpen] = useState(false)
  const [levelTab, setLevelTab] = useState<'hsk' | 'newHsk' | 'tofcl'>('newHsk')
  const popoverRef = useRef<HTMLDivElement>(null)

  // Keep local search in sync with external resets (e.g. URL nav).
  useEffect(() => {
    setLocalSearch(search)
  }, [search])

  const handleSearch = useCallback(
    (val: string) => {
      setLocalSearch(val)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => onSearchChange(val), 300)
    },
    [onSearchChange],
  )

  const toggleChip = (val: string) => onMasteryChange(toggleIn(mastery, val))

  // Dismiss the level popover on outside click / Escape.
  useEffect(() => {
    if (!levelOpen) return
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) setLevelOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLevelOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [levelOpen])

  const levelCount = levels.hsk.length + levels.newHsk.length + levels.tofcl.length

  const activeLevelPills = useMemo(() => {
    const pills: { key: string; label: string; remove: () => void }[] = []
    levels.hsk.forEach((l) =>
      pills.push({
        key: `h-${l}`,
        label: `${EXAM.hsk} ${l}`,
        remove: () => onLevelsChange({ hsk: levels.hsk.filter((x) => x !== l) }),
      }),
    )
    levels.newHsk.forEach((l) =>
      pills.push({
        key: `nh-${l}`,
        label: `${EXAM.newHsk} ${l}`,
        remove: () => onLevelsChange({ newHsk: levels.newHsk.filter((x) => x !== l) }),
      }),
    )
    levels.tofcl.forEach((l) => {
      const short = TOCFL_LEVELS.find((t) => t.value === l)?.short || l
      pills.push({
        key: `t-${l}`,
        label: `${EXAM.tocfl} ${short}`,
        remove: () => onLevelsChange({ tofcl: levels.tofcl.filter((x) => x !== l) }),
      })
    })
    return pills
  }, [levels, onLevelsChange])

  const clearLevels = () => onLevelsChange({ hsk: [], newHsk: [], tofcl: [] })

  return (
    <div className='words-filters'>
      <div className='words-filters-row'>
        <div className='words-search-wrap'>
          <svg
            className='words-search-icon'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <circle cx='11' cy='11' r='7' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input
            className='words-search-input'
            placeholder={t('filters.searchPlaceholder')}
            value={localSearch}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete='off'
            inputMode='search'
          />
          {localSearch && (
            <button
              type='button'
              className='words-search-clear'
              onClick={() => handleSearch('')}
              aria-label={t('filters.clearSearch')}
            >
              {CROSS}
            </button>
          )}
        </div>

        <div className='words-filters-actions'>
          <div className='words-mastery-chips'>
            {masteryChips.map((c) => (
              <button
                key={c.value}
                className={`words-chip${mastery.includes(c.value) ? ' active' : ''}`}
                onClick={() => toggleChip(c.value)}
                aria-pressed={mastery.includes(c.value)}
              >
                {t(c.label)}
              </button>
            ))}
          </div>

          <button
            type='button'
            className={`words-chip words-struggling-chip${struggling ? ' active' : ''}`}
            onClick={() => onStrugglingChange(!struggling)}
            aria-pressed={struggling}
            title={t('filters.strugglingTitle')}
          >
            <svg
              width='11'
              height='11'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
              focusable='false'
            >
              <path d='M12 3 2 20h20L12 3z' />
              <line x1='12' y1='10' x2='12' y2='14' />
              <line x1='12' y1='17' x2='12.01' y2='17' />
            </svg>
            {t('filters.struggling')}
          </button>

          <div className='words-level-filter' ref={popoverRef}>
            <button
              type='button'
              className={`words-chip words-level-trigger${levelCount ? ' active' : ''}`}
              onClick={() => setLevelOpen((o) => !o)}
              aria-expanded={levelOpen}
              aria-haspopup='dialog'
            >
              {t('filters.level')}
              {levelCount > 0 && <span className='words-level-trigger-count'>{levelCount}</span>}
              <svg
                width='10'
                height='10'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
                style={{
                  marginLeft: 4,
                  transform: levelOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s',
                }}
              >
                <polyline points='6 9 12 15 18 9' />
              </svg>
            </button>

            {levelOpen && (
              <>
                <div className='words-info-popover-scrim' aria-hidden='true' onClick={() => setLevelOpen(false)} />
                <div className='words-level-popover' role='dialog' aria-label={t('filters.levelDialog')}>
                  <div className='words-level-tabs'>
                    <button
                      className={levelTab === 'newHsk' ? 'active' : ''}
                      onClick={() => setLevelTab('newHsk')}
                      type='button'
                    >
                      {EXAM.newHsk}
                      {levels.newHsk.length > 0 && (
                        <span className='words-level-tab-count'>{levels.newHsk.length}</span>
                      )}
                    </button>
                    <button
                      className={levelTab === 'hsk' ? 'active' : ''}
                      onClick={() => setLevelTab('hsk')}
                      type='button'
                    >
                      {EXAM.hsk}
                      {levels.hsk.length > 0 && <span className='words-level-tab-count'>{levels.hsk.length}</span>}
                    </button>
                    <button
                      className={levelTab === 'tofcl' ? 'active' : ''}
                      onClick={() => setLevelTab('tofcl')}
                      type='button'
                    >
                      {EXAM.tocfl}
                      {levels.tofcl.length > 0 && <span className='words-level-tab-count'>{levels.tofcl.length}</span>}
                    </button>
                  </div>

                  <div className='words-level-grid'>
                    {levelTab === 'hsk' &&
                      HSK_LEVELS.map((l) => (
                        <button
                          key={l}
                          type='button'
                          className={`words-level-chip${levels.hsk.includes(l) ? ' active' : ''}`}
                          onClick={() => onLevelsChange({ hsk: toggleIn(levels.hsk, l) })}
                        >
                          {`${EXAM.hsk} ${l}`}
                        </button>
                      ))}
                    {levelTab === 'newHsk' &&
                      NEW_HSK_LEVELS.map((l) => (
                        <button
                          key={l}
                          type='button'
                          className={`words-level-chip${levels.newHsk.includes(l) ? ' active' : ''}`}
                          onClick={() => onLevelsChange({ newHsk: toggleIn(levels.newHsk, l) })}
                        >
                          {`${EXAM.newHsk} ${l}`}
                        </button>
                      ))}
                    {levelTab === 'tofcl' &&
                      TOCFL_LEVELS.map((level) => (
                        <button
                          key={level.value}
                          type='button'
                          className={`words-level-chip${levels.tofcl.includes(level.value) ? ' active' : ''}`}
                          onClick={() => onLevelsChange({ tofcl: toggleIn(levels.tofcl, level.value) })}
                          title={level.value}
                        >
                          {`${EXAM.tocfl} ${level.short}`}
                        </button>
                      ))}
                  </div>

                  <div className='words-level-foot'>
                    <span className='words-level-foot-hint'>{t('filters.levelHint')}</span>
                    {levelCount > 0 && (
                      <button type='button' className='words-level-clear' onClick={clearLevels}>
                        {t('filters.clearAll')}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {activeLevelPills.length > 0 && (
        <div className='words-active-pills' aria-label={t('filters.activeLevels')}>
          {activeLevelPills.map((p) => (
            <button
              key={p.key}
              type='button'
              className='words-active-pill'
              onClick={p.remove}
              aria-label={t('filters.removeLevel', { level: p.label })}
            >
              {p.label} <span aria-hidden='true'>{CROSS}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default WordsFilters
